import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Maximize2,
  Minimize2,
  StickyNote,
} from "lucide-react";

/**
 * Navigation — overlay UI for the keynote:
 *   - Edge click zones (left/right halves)
 *   - Floating control bar (prev / count / next / grid / notes / fullscreen)
 *   - Top progress bar
 *   - Bottom slide-indicator dots
 */
export const Navigation = ({
  index,
  total,
  onPrev,
  onNext,
  onJump,
  onTogglePresenter,
  onToggleFullscreen,
  onOpenPresenterView,
  isFullscreen,
  scenes,
}) => {
  const progress = total > 0 ? ((index + 1) / total) * 100 : 0;

  // Auto-hide UI when the cursor is idle. The bottom controls + the bottom
  // dots both fade out together for a calm, distraction-free stage.
  // Any mouse movement (or focus) wakes them up; idle for 2.4s hides them.
  const [uiVisible, setUiVisible] = useState(true);
  const [hoverPersist, setHoverPersist] = useState(false);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    const ping = () => {
      setUiVisible(true);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        setUiVisible(false);
      }, 2400);
    };
    ping();
    window.addEventListener("mousemove", ping);
    window.addEventListener("touchstart", ping);
    window.addEventListener("keydown", ping);
    return () => {
      window.removeEventListener("mousemove", ping);
      window.removeEventListener("touchstart", ping);
      window.removeEventListener("keydown", ping);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  // While hovering the chrome itself, never hide.
  const showChrome = uiVisible || hoverPersist;

  return (
    <>
      {/* Top progress */}
      <div className="absolute top-0 left-0 right-0 z-30 pointer-events-none">
        <div className="h-[3px] w-full bg-white/5">
          <motion.div
            className="h-full"
            style={{
              background:
                "linear-gradient(90deg, #96C11F 0%, #00deb6 100%)",
            }}
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            data-testid="progress-bar"
          />
        </div>
      </div>

      {/* Edge click zones */}
      <button
        type="button"
        aria-label="Previous slide"
        onClick={onPrev}
        className="edge-zone absolute top-0 bottom-0 left-0 w-[12%] z-20 cursor-arrow-left"
        data-testid="edge-zone-prev"
      />
      <button
        type="button"
        aria-label="Next slide"
        onClick={onNext}
        className="edge-zone right absolute top-0 bottom-0 right-0 w-[12%] z-20 cursor-arrow-right"
        data-testid="edge-zone-next"
      />

      {/* Bottom dots — fade in/out with the rest of the chrome */}
      <AnimatePresence>
        {showChrome && (
          <motion.div
            key="dots"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute bottom-7 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2"
            onMouseEnter={() => setHoverPersist(true)}
            onMouseLeave={() => setHoverPersist(false)}
          >
            {scenes?.map((s, i) => {
              const isActive = i === index;
              return (
                <button
                  type="button"
                  key={s.id}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => onJump?.(i)}
                  className="group p-1.5"
                  data-testid={`dot-${i}`}
                >
                  <span
                    className="block rounded-full transition-all duration-300"
                    style={{
                      width: isActive ? 28 : 8,
                      height: 8,
                      background: isActive
                        ? "linear-gradient(90deg, #96C11F, #00deb6)"
                        : "rgba(255,255,255,0.25)",
                    }}
                  />
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating controls bottom-right — fade with the rest of the chrome */}
      <AnimatePresence>
        {showChrome && (
          <motion.div
            key="controls"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute bottom-6 right-6 z-30 glass-dark rounded-full px-2 py-2 flex items-center gap-1"
            onMouseEnter={() => setHoverPersist(true)}
            onMouseLeave={() => setHoverPersist(false)}
            data-testid="floating-controls"
          >
        <ControlButton
          onClick={onPrev}
          ariaLabel="Previous"
          testId="ctrl-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </ControlButton>
        <div className="px-3 text-xs tracking-[0.28em] uppercase text-white/70 tnum">
          {String(index + 1).padStart(2, "0")}
          <span className="mx-1.5 text-white/30">/</span>
          {String(total).padStart(2, "0")}
        </div>
        <ControlButton
          onClick={onNext}
          ariaLabel="Next"
          testId="ctrl-next"
        >
          <ChevronRight className="w-5 h-5" />
        </ControlButton>
        <span className="w-px h-6 bg-white/10 mx-1" />
        <ControlButton
          onClick={onTogglePresenter}
          ariaLabel="Open presenter overview"
          testId="ctrl-presenter"
        >
          <LayoutGrid className="w-4.5 h-4.5" />
        </ControlButton>
        <ControlButton
          onClick={onOpenPresenterView}
          ariaLabel="Open speaker notes (second window)"
          testId="ctrl-notes"
        >
          <StickyNote className="w-4.5 h-4.5" />
        </ControlButton>
        <ControlButton
          onClick={onToggleFullscreen}
          ariaLabel="Toggle fullscreen"
          testId="ctrl-fullscreen"
        >
          {isFullscreen ? (
            <Minimize2 className="w-4.5 h-4.5" />
          ) : (
            <Maximize2 className="w-4.5 h-4.5" />
          )}
        </ControlButton>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const ControlButton = ({ children, onClick, ariaLabel, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    data-testid={testId}
    className="w-9 h-9 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200"
  >
    {children}
  </button>
);

export default Navigation;
